/**
 * Keeping the ChaosTree session alive.
 *
 * ── Why this has to exist here ──────────────────────────────────────────────
 *
 * Every other page on the site loads `base.js`, which pings `/api/auth/refresh`
 * once a minute against a session that lasts five. The game does not: it is a
 * Vite build with its own `index.html`, so none of that script runs, and
 * nothing was refreshing anything.
 *
 * The result was a session that died about five minutes into playing — long
 * enough to look like an unrelated bug, and reliably fatal to a long session,
 * which is the only kind this game has.
 *
 * Pulling in `base.js` would fix it and drag along the shader background, the
 * glitch effect and a config object built for a different page. This is the
 * same two requests without any of that.
 *
 * ── Resuming ────────────────────────────────────────────────────────────────
 *
 * A lapse is recoverable. If the browser still holds the month-long remember-me
 * token, `/api/auth/resume` trades it for a fresh session — the same fallback
 * `base.js` uses, and worth mirroring, or "keep me signed in" would work
 * everywhere on the site except in the game.
 */

/** How often to refresh. A fifth of the five-minute session, as elsewhere. */
export const KEEPALIVE_INTERVAL = 60_000;

const REFRESH_ENDPOINT = '/api/auth/refresh';
const RESUME_ENDPOINT = '/api/auth/resume';

/** Cookies the site sets without `httpOnly`, so the client can read them. */
function cookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? '') : null;
}

export type SessionEvent = 'alive' | 'resumed' | 'lost';

/**
 * Refresh once, resuming from a remembered device if the session has lapsed.
 *
 * Exported so the caller can force a check — worth doing when a tab comes back
 * to the foreground, since a laptop that slept through two intervals is exactly
 * the case a fixed timer misses.
 */
export async function touchSession(): Promise<SessionEvent> {
  try {
    const refreshed = await fetch(REFRESH_ENDPOINT, { method: 'POST', credentials: 'include' });
    if (refreshed.ok) return 'alive';
  } catch {
    // Offline, or the backend is down. Either way it is worth trying to resume
    // rather than declaring the session dead on one failed request.
  }

  if (cookie('hasRemember') !== 'true') return 'lost';

  try {
    const resumed = await fetch(RESUME_ENDPOINT, { method: 'POST', credentials: 'include' });
    return resumed.ok ? 'resumed' : 'lost';
  } catch {
    return 'lost';
  }
}

/**
 * Keep the session alive for as long as the game is open.
 *
 * Returns a stop function. Also refreshes whenever the tab becomes visible
 * again: `setInterval` is throttled hard in background tabs and does not run
 * at all while a machine is asleep, so a fixed timer alone would let exactly
 * the sessions that matter most quietly expire.
 */
export function keepSessionAlive(onEvent?: (event: SessionEvent) => void): () => void {
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    void touchSession().then((event) => {
      if (!stopped) onEvent?.(event);
    });
  };

  // Immediately, so a session already close to lapsing when the game opens is
  // caught rather than waiting out the first interval.
  tick();

  const timer = window.setInterval(tick, KEEPALIVE_INTERVAL);
  const onVisible = () => {
    if (document.visibilityState === 'visible') tick();
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
